import React, { useEffect, useState } from "react";
import OrderLinesTable from "../../../../components/Orders/OrderLinesTable";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import { withStyles } from "@material-ui/core/styles";
import { FormControlLabel, Switch } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  MdCancel,
  MdLocalPrintshop,
  MdSportsMotorsports,
  MdArrowBackIosNew,
} from "react-icons/md";
import {
  assignRestaurantOrderToDeliveryAgent,
  getOrderDetailsToProcess,
  markOrderAsPaid,
  updateRestaurantOrderDeliveryStatus,
} from "../../../../actions/webOrderActions";
import { toast } from "react-toastify";
import { RESET_UPDATE_DELIVERY_STATUS_COMPLETE } from "../../../../constants/webOrderConstants";
import "./RestaurantOrderDetails.css";
import { getUserFromLocalStorage } from "utilities/user";
import CancelOrderFields, { useCancellationReason, CancellationRecord } from "../../../../components/Orders/CancelOrderFields";
import ContactAction from "../../../../components/Orders/ContactAction";
import DropoffMap from "../../../../components/Maps/DropoffMap";
import {
  getDeliveryProgress,
  canVendorDispatch,
  canVendorMarkDelivered,
  AUDIENCE,
} from "../../../../utilities/orderDeliverySteps";
import OrderNextStep from "../../../../components/Orders/OrderNextStep";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import AppSelect from "../../../../components/ui/AppSelect";
import OrderAssignmentPanel from "../../../../components/Orders/OrderAssignmentPanel";
import VendorHandoffActions from "../../../../components/Orders/VendorHandoffActions";
import { getShippingAddressText, getPaymentMethodLabel } from "../../../../utilities/orderPresentation";
import { Fact } from "../../../../components/Orders/OrderPanel";
import OrderMoneyPanel from "../../../../components/Orders/OrderMoneyPanel";
// Type on this page comes from the console scale (--lk-text-* in
// assets/css/lusuku-overrides.css): titles --lk-text-md, body --lk-text,
// button text --lk-text-sm, icon glyphs 16px. The inline 20/19/14px sizes it
// carried sat beside 13px panels and read as a different app (25 Oct 2026).
import { PaymentStateBadge, DeliveryStateBadge } from "../../../../components/Orders/StateBadge";
import { riderBadgeProps, riderName, sortRidersOnlineFirst } from "../../../../components/Orders/riderStatus";
import { openReceiptForPrint } from "../../../../utilities/openReceipt";

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#52d869",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked,
      }}
      {...props}
    />
  );
});

const isDevelopment = process.env.NODE_ENV === "development";

const BrandOrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = getUserFromLocalStorage();

  const [showCancelModal, setShowCancelModal] = useState(false);
  // Reason + note for the audit record. Required by the server on cancel.
  const cancelReason = useCancellationReason();
  const [riders, setRiders] = useState([]);
  const [riderId, setRiderId] = useState("");
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [assignedRider, setAssignedRider] = useState(null);
  const [elapsedAssignedTime, setElapsedAssignedTime] = useState(0);
  const [elapsedDispatchedTime, setElapsedDispatchedTime] = useState(0);

  const orderDetails = useSelector((state) => state.orderDetails);
  const {
    order,
    loading,
    error,
    isUpdateDeliveryStatusComplete,
    paymentResult,
  } = orderDetails;

  // Live order news (a rider's pickup, a 3PL delivery, a decline) through the
  // one shared feed; see hooks/useOrderLiveUpdates.js.
  useOrderLiveUpdates(id, {
    onChange: (fresh) => {
      if (fresh?.delivery_status === "PROCESSING" && !fresh?.riderId) setRiderId("");
    },
  });

  useEffect(() => {
    dispatch(getOrderDetailsToProcess(id));
    if (isUpdateDeliveryStatusComplete) {
      dispatch(getOrderDetailsToProcess(id));
      dispatch({ type: RESET_UPDATE_DELIVERY_STATUS_COMPLETE });
    }
  }, [isUpdateDeliveryStatusComplete, id, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (order?.riderId && riders.length) {
      setAssignedRider(
        riders.find(
          (rider) =>
            rider._id ===
            (typeof order.riderId === "object"
              ? order.riderId._id
              : order.riderId)
        )
      );
    }
  }, [order, riders]);

  const socketUrl = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    const socket = io(
      socketUrl,
      {
        query: {
          user: user?.result?._id,
        },
      },
      {
        transports: ["websocket"],
      }
    );

    socket.on("connect", () => {
      if (isDevelopment) {
        console.log("Webclient Connected");
      }
      socket.emit("getRiders");
    });

    socket.on("initialRidersList", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    socket.on("lusukuRiders", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    socket.on("updatedRidersList", (updatedRiders) => {
      setRiders(updatedRiders);
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.result?._id]);

  const handleBack = () => {
    const isSuperAdmin =
      user?.result?.user_role === "SUPERUSER" ||
      user?.result?.userType === "LUSUKU STAFF";

    if (isSuperAdmin) {
      history.push("/admin/paginatedOrdersTable");
    } else {
      history.push("/admin/brand-orders");
    }
  };

  const handleUpdateStatus = (newStatus) => {
    if (newStatus === "ASSIGNED" && !riderId) {
      toast.warn("Please select a rider before assigning the order.");
      return;
    }
    if (newStatus === "ASSIGNED") {
      assignBrandOrderToRider();
    } else {
      dispatch(updateRestaurantOrderDeliveryStatus(order?._id, newStatus));
    }
  };

  const handleCancelOrder = () => {
    // Refuse locally too, so the modal explains itself instead of
    // bouncing off a 400 from the server.
    if (!cancelReason.validate()) return;
    const delivery_status = "REJECTED";
    
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status, cancelReason.payload()));
    setShowCancelModal(false);
    cancelReason.reset();
  };

  // The receipt document (seller, TIN, customer, payment, lines, VAT, URA
  // markings when issued), not a print of this page.
  const handlePrint = () => openReceiptForPrint(order?._id);

  const handleMarkAsPaid = () => {
    if (user?.result?.userType !== "LUSUKU STAFF" && user?.result?.user_role !== "SUPERUSER") {
      toast.warn("🚫 You are not authorized to mark order as paid!");
      return;
    } else {
      dispatch(markOrderAsPaid(order?._id));
    }
  };

  const assignBrandOrderToRider = () => {
    const delivery_status = "ASSIGNED";
    const assignedTime = Date.now();
    toast.success(`Assigning order ${order?._id} to rider ${riderId}`);
    localStorage.setItem(`order_${order?._id}_assignedTime`, assignedTime);
    dispatch(
      assignRestaurantOrderToDeliveryAgent(order?._id, riderId, delivery_status)
    );
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleDispatchBrandOrder = () => {
    const delivery_status = "DISPATCHED";
    const dispatchedTime = Date.now();
    console.log(`Dispatching order ${order?._id}`);
    localStorage.setItem(`order_${order?._id}_dispatchedTime`, dispatchedTime);
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleDeliverBrandOrder = () => {
    const delivery_status = "DELIVERED";
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  // The delivery ladder lives in utilities/orderDeliverySteps.js. This was a
  // four-case switch whose `default: 0` emptied the bar for the six statuses
  // this page never writes itself: ACCEPTED and PICKED_UP come from the rider
  // app, PENDING_3PL / IN_TRANSIT / ARRIVED from the 3PL rail, FULFILLED from
  // the counter. A rider tapping Accept made the order look like it had gone
  // backwards to zero.
  //
  // This is an ADMIN surface, so it takes the progress and the button rules but
  // NOT the vendor-facing OrderNextStep banner, whose copy ("Your turn", "press
  // Dispatch Order") is addressed to the vendor working the order.
  //
  // NOTE: there is no "PAID" delivery_status. Payment state lives in
  // `isPaid`/`paymentStatus` (see utilities/paymentStatus.js); the
  // delivery_status enum in models/Orders.js has no such member.
  const getProgress = () => getDeliveryProgress(order);

  // Only an order still in PROCESSING can be cancelled here. This used to also
  // accept a "PAID" delivery_status, which is not a member of the delivery_status
  // enum and could never be stored — dead code that made the rule look broader
  // than it is. Behaviour is unchanged.
  const canCancelOrder = order?.delivery_status === "PROCESSING";

  const getDisabledStatusOptions = () => {
    const disabledOptions = {
      PROCESSING: order?.delivery_status !== "PROCESSING",
      ASSIGNED:
        order?.delivery_status !== "PROCESSING" &&
        !canVendorDispatch(order),
      DISPATCHED:
        !canVendorDispatch(order) &&
        !canVendorMarkDelivered(order),
      DELIVERED:
        !canVendorMarkDelivered(order) &&
        order?.delivery_status !== "DELIVERED",
      REJECTED:
        order?.delivery_status === "DELIVERED" ||
        order?.delivery_status === "REJECTED",
    };
    return disabledOptions;
  };

  const disabledStatusOptions = getDisabledStatusOptions();

  useEffect(() => {
    if (order?.delivery_status === "ASSIGNED") {
      const storedAssignedTime = localStorage.getItem(
        `order_${order?._id}_assignedTime`
      );
      if (storedAssignedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedAssignedTime, 10)) / 1000
        );
        setElapsedAssignedTime(elapsed);
      }
    }
    if (order?.delivery_status === "DISPATCHED") {
      const storedDispatchedTime = localStorage.getItem(
        `order_${order?._id}_dispatchedTime`
      );
      if (storedDispatchedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedDispatchedTime, 10)) / 1000
        );
        setElapsedDispatchedTime(elapsed);
      }
    }

    const interval = setInterval(() => {
      if (order?.delivery_status === "ASSIGNED") {
        setElapsedAssignedTime((prev) => prev + 1);
      }
      if (order?.delivery_status === "DISPATCHED") {
        setElapsedDispatchedTime((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.delivery_status, order?._id]);

  // Get brand name from order items
  const getBrandName = () => {
    if (order?.orderItems && order.orderItems.length > 0) {
      const firstItem = order.orderItems[0];
      if (firstItem.vendorId) {
        if (typeof firstItem.vendorId === "object") {
          return firstItem.vendorId.name || firstItem.vendorId.businessName || "Brand";
        }
      }
    }
    return "Brand";
  };

  if (loading) {
    return (
      <Spinner
        animation="grow"
        variant="primary"
        style={{
          marginTop: "70px",
          top: "50%",
          left: "50%",
          position: "fixed",
        }}
      />
    );
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: "70px" }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-2">
        <Col md="12">
          <Button
            variant="secondary"
            onClick={handleBack}
            style={{
              backgroundColor: "#4B3",
              color: "white",
              border: "none",
              fontSize: "var(--lk-text-sm)",
              padding: "10px",
            }}
          >
            <MdArrowBackIosNew
              style={{
                marginRight: "3px",
                fontSize: "16px",
                color: "#FFFF",
                fontWeight: "bold",
              }}
            />
            Back to Orders
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <Card className="shadow-sm">
            <Card.Header
              className="justify-content-between align-items-center"
              style={{
                backgroundColor: "#012030",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Card.Title as="h4" style={{ color: "white", marginBottom: "4px" }}>
                  Brand Order Details - Order #{order?._id}
                </Card.Title>
                {order?.createdAt && (
                  <small style={{ color: "#a8c6df", display: "block" }}>
                    {moment(order.createdAt).format("MMM D, YYYY · h:mm a")}
                  </small>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <VendorHandoffActions orderId={order?._id} />
                <Button
                  style={{
                    backgroundColor: "#13678A",
                    color: "white",
                    border: "none",
                    fontSize: "var(--lk-text-sm)",
                    padding: "10px",
                  }}
                  onClick={handlePrint}
                >
                  Print Receipt
                  <MdLocalPrintshop style={{ marginLeft: "4px" }} />
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md="6">
                  <h5
                    style={{
                      color: "#023059",
                      fontSize: "var(--lk-text-md)",
                      fontWeight: 600,
                    }}
                  >
                    Order Items
                  </h5>
                  <OrderLinesTable order={order} />
                  <OrderAssignmentPanel key={id} order={order}
                    onChange={() => { setRiderId(""); dispatch(getOrderDetailsToProcess(id)); }}>
                    <Form.Group
                      controlId="formRiderSelect"
                      className="mb-0"
                    >
                      <Form.Label>Select Rider</Form.Label>
                      <AppSelect block
                        id="formRiderSelect"
                        value={
                          order?.riderId?._id ||
                          (typeof order?.riderId === "object"
                            ? order?.riderId?._id
                            : order?.riderId) ||
                          riderId
                        }
                        onChange={(e) => setRiderId(e.target.value)}
                        disabled={!!order?.riderId || order?.delivery_status !== "PROCESSING"}
                      >
                        <option value="">Select Rider</option>
                        {loadingRiders ? (
                          <option value="" disabled>
                            Searching for riders...
                          </option>
                        ) : (
                          sortRidersOnlineFirst(riders).map((rider) => (
                            <option key={rider._id} value={rider._id} {...riderBadgeProps(rider)}>
                              {riderName(rider)}
                            </option>
                          ))
                        )}
                      </AppSelect>
                      {loadingRiders && (
                        <div style={{ marginLeft: "2px" }}>
                          <Spinner animation="border" size="sm" />
                        </div>
                      )}
                      {order?.riderId && (
                        <p
                          style={{
                            marginTop: "10px",
                            fontSize: "var(--lk-text-sm)",
                            fontStyle: "italic",
                          }}
                        >
                          Assigned to:{" "}
                          {typeof order.riderId === "object"
                            ? order.riderId.firstName + " " + order.riderId.lastName
                            : "Rider"}
                          <MdSportsMotorsports
                            style={{
                              marginLeft: "5px",
                              fontSize: "16px",
                              color: "green",
                            }}
                          />
                          <br />
                          {order?.delivery_status === "ASSIGNED" && (
                            <span>
                              Time Elapsed after Assigning:{" "}
                              {Math.floor(elapsedAssignedTime / 3600)}h{" "}
                              {Math.floor((elapsedAssignedTime % 3600) / 60)}m{" "}
                              {elapsedAssignedTime % 60}s
                            </span>
                          )}
                          {order?.delivery_status === "DISPATCHED" && (
                            <span>
                              Time Elapsed from Dispatch:{" "}
                              {Math.floor(elapsedDispatchedTime / 3600)}h{" "}
                              {Math.floor((elapsedDispatchedTime % 3600) / 60)}m{" "}
                              {elapsedDispatchedTime % 60}s
                            </span>
                          )}
                        </p>
                      )}
                      {!order?.riderId && riderId && order?.delivery_status === "PROCESSING" && (
                        <Button
                          variant="primary"
                          onClick={assignBrandOrderToRider}
                          className="mt-2"
                          disabled={!riderId || loadingRiders}
                        >
                          Assign Order to Rider
                        </Button>
                      )}
                    </Form.Group>
                  </OrderAssignmentPanel>
                </Col>
                <Col md="6">
                  <h5
                    style={{
                      color: "#023059",
                      fontSize: "var(--lk-text-md)",
                      fontWeight: 600,
                    }}
                  >
                    Order Summary
                  </h5>
                  {/* Rebuilt 25 Oct 2026 on the shared order-page pieces
                      (OrderPanel's facts grid, OrderMoneyPanel, StateBadge)
                      instead of nine ragged "Label: value" paragraphs:
                      - the facts sit in a two-column grid that reads in
                        pairs and collapses to one column on a phone;
                      - payment status is a worded pill (PAID, PENDING, COD,
                        FAILED, NOT CHARGED) from utilities/paymentStatus,
                        not a bare card icon whose colour had to be known;
                      - the payment method is named (MTN Mobile Money), not
                        the stored enum (MOMO);
                      - the money is OrderMoneyPanel, so items, delivery fee,
                        any processing fee and the total add up on screen,
                        with VAT noted as contained in the total; shillings
                        print without the ".00" that suggested cents;
                      - the short order reference matches the receipt and
                        the apps, with the full id on hover. */}
                  <Card
                    className="mb-3 p-3 shadow"
                    style={{ boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)" }}
                  >
                    <div className="rod-facts">
                      <Fact label="Brand" value={getBrandName()} />
                      <Fact
                        label="Order"
                        value={
                          order?._id ? (
                            <span title={String(order._id)}>
                              #{String(order._id).slice(-6).toUpperCase()}
                            </span>
                          ) : null
                        }
                      />
                      <Fact label="Customer" value={order?.customerName || "Not recorded"} />
                      <Fact
                        label="Placed"
                        value={
                          order?.createdAt
                            ? moment(order.createdAt).format("D MMM YYYY, h:mm a")
                            : "Not recorded"
                        }
                      />
                      <Fact label="Payment method" value={getPaymentMethodLabel(order?.paymentMethod)} />
                      <Fact
                        label="Payment status"
                        value={
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <PaymentStateBadge order={order} />
                            {order?.paidAt ? (
                              <span style={{ color: "#64748b", fontSize: "var(--lk-text-sm)" }}>
                                {moment(order.paidAt).format("D MMM YYYY, h:mm a")}
                              </span>
                            ) : null}
                          </span>
                        }
                      />
                      <Fact
                        label="Order status"
                        value={<DeliveryStateBadge status={order?.delivery_status || "PROCESSING"} />}
                      />
                      <Fact label="Placed from" value={order?.orderSrc} />
                    </div>
                    <div style={{ marginTop: 14 }}>
                      <OrderMoneyPanel order={order} />
                    </div>
                  </Card>
                  <h5
                    style={{
                      color: "#023059",
                      fontSize: "var(--lk-text-md)",
                      fontWeight: 600,
                    }}
                  >
                    Shipping Address
                  </h5>
                  <Card
                    className="p-3 shadow"
                    style={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)" }}
                  >
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      {getShippingAddressText(order?.shippingAddress, "No address recorded")}
                    </p>
                    <ContactAction
                      label="Customer"
                      name={order?.customerName}
                      phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                      compact
                      style={{ marginBottom: 10 }}
                    />

                    {/* The same drop-off block as the admin order page:
                        place name, precision chip, Show map, Send pin,
                        Copy pin. This page printed the raw coordinates
                        instead, which nobody can read. */}
                    {Number.isFinite(Number(order?.shippingAddress?.latLng?.lat)) &&
                      Number.isFinite(Number(order?.shippingAddress?.latLng?.lng)) && (
                        <DropoffMap
                          lat={order.shippingAddress.latLng.lat}
                          lng={order.shippingAddress.latLng.lng}
                          address={getShippingAddressText(order?.shippingAddress, "")}
                          shippingAddress={order?.shippingAddress}
                          orderRef={order?.order_number || order?._id}
                          rider={assignedRider || order?.riderId}
                          country={order?.country}
                        />
                      )}
                  </Card>
                  <Form.Group
                    controlId="formOrderStatus"
                    className="shadow p-3 mt-3"
                    style={{ boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)" }}
                  >
                    <Form.Label>Update Order Status</Form.Label>
                    <AppSelect block
                      id="formOrderStatus"
                      value={order?.delivery_status || "PROCESSING"}
                      onChange={(e) => handleUpdateStatus(e.target.value)}
                    >
                      {order?.delivery_status && !["PROCESSING", "ASSIGNED", "DISPATCHED", "DELIVERED", "REJECTED"].includes(order.delivery_status) && (
                        <option value={order.delivery_status} disabled>
                          {order.delivery_status.replace(/_/g, " ")}
                        </option>
                      )}
                      <option
                        value="PROCESSING"
                        disabled={disabledStatusOptions.PROCESSING}
                      >
                        1. Processing
                      </option>
                      <option
                        value="ASSIGNED"
                        disabled={disabledStatusOptions.ASSIGNED}
                      >
                        2. Assigned
                      </option>
                      <option
                        value="DISPATCHED"
                        disabled={disabledStatusOptions.DISPATCHED}
                      >
                        3. Dispatched
                      </option>
                      <option
                        value="DELIVERED"
                        disabled={disabledStatusOptions.DELIVERED}
                      >
                        4. Delivered
                      </option>
                      <option
                        value="REJECTED"
                        disabled={disabledStatusOptions.REJECTED}
                      >
                        5. Cancel Order
                      </option>
                    </AppSelect>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-4">
                <Col className="d-flex justify-content-end align-items-center">
                  <div
                    style={{
                      cursor: !canCancelOrder ? "not-allowed" : "pointer",
                      backgroundColor: !canCancelOrder
                        ? "#8C1F28"
                        : "transparent",
                      borderRadius: "5px",
                      padding: "2px",
                      marginRight: "5px",
                      width: "160px",
                      height: "40px",
                    }}
                  >
                    <Button
                      variant="danger"
                      onClick={() => setShowCancelModal(true)}
                      className="mr-2"
                      disabled={!canCancelOrder}
                      style={{
                        backgroundColor: !canCancelOrder
                          ? "transparent"
                          : "red",
                        color: !canCancelOrder ? "white" : "white",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                        width: "100%",
                        height: "100%",
                        padding: "6px",
                      }}
                    >
                      <MdCancel
                        style={{
                          fontSize: "16px",
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: "5px",
                        }}
                      />
                      Cancel Order
                    </Button>
                  </div>
                  {/* components/Orders/ContactAction: a real tel: link on a
                      phone, copy on a desktop. The old button opened tel: on
                      every device, which does nothing on the desktop this
                      page is mostly read from. */}
                  <ContactAction
                    label="Customer"
                    name={order?.customerName}
                    phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                    style={{ marginRight: 5 }}
                  />
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md="12">
                  {/* Admin voice: this reports what the vendor and rider are doing.
                      See the AUDIENCE note in utilities/orderDeliverySteps.js. */}
                  <OrderNextStep
                    order={order}
                    audience={AUDIENCE.ADMIN}
                    style={{ marginBottom: "12px" }}
                  />
                  <ProgressBar
                    now={getProgress()}
                    label={`${getProgress()}%`}
                    style={{ height: "30px" }}
                    variant={
                      order?.delivery_status === "DELIVERED"
                        ? "success"
                        : undefined
                    }
                  />
                </Col>
              </Row>
              <Row className="mt-4">
                <Col md="12" className="d-flex justify-content-between">
                  <div
                    style={{
                      cursor:
                        !canVendorDispatch(order)
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor:
                        !canVendorDispatch(order)
                          ? "#4B4952"
                          : "transparent",
                      borderRadius: "5px",
                      padding: "2px",
                    }}
                  >
                    <Button
                      variant="warning"
                      onClick={handleDispatchBrandOrder}
                      disabled={!canVendorDispatch(order)}
                      style={{
                        backgroundColor:
                          !canVendorDispatch(order)
                            ? "transparent"
                            : "orange",
                        color:
                          !canVendorDispatch(order)
                            ? "white"
                            : "black",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                      }}
                    >
                      Dispatch Order
                    </Button>
                  </div>
                  <div
                    style={{
                      cursor:
                        !canVendorMarkDelivered(order)
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor:
                        !canVendorMarkDelivered(order)
                          ? "green"
                          : "transparent",
                      borderRadius: "5px",
                      padding: "2px",
                    }}
                  >
                    <Button
                      variant="success"
                      onClick={handleDeliverBrandOrder}
                      disabled={!canVendorMarkDelivered(order)}
                      style={{
                        backgroundColor:
                          !canVendorMarkDelivered(order)
                            ? "transparent"
                            : "green",
                        color:
                          !canVendorMarkDelivered(order)
                            ? "white"
                            : "black",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                      }}
                    >
                      Mark as Delivered
                    </Button>
                  </div>
                  <div
                    style={{
                      cursor: order?.isPaid ? "not-allowed" : "pointer",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <IOSSwitch
                          checked={order?.isPaid || false}
                          onChange={handleMarkAsPaid}
                          color="primary"
                          disabled={
                            order?.delivery_status !== "DELIVERED" ||
                            order?.isPaid
                          }
                        />
                      }
                      label={order?.isPaid ? "Paid" : "Mark as Paid"}
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showCancelModal} onHide={() => { setShowCancelModal(false); cancelReason.reset(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel this order?
          <CancelOrderFields state={cancelReason} isPaid={!!order?.isPaid} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Close
          </Button>
          <Button variant="danger" onClick={handleCancelOrder}>
            Cancel Order
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BrandOrderDetails;
